import React, { useState, useEffect } from 'react';
import { getFirestore, collection, doc, setDoc, deleteDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';

const SitePromotionsTab = ({ user, location, publicStore }) => {
  const [promotions, setPromotions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'percentage',
    value: '',
    minOrder: '',
    startDate: '',
    endDate: '',
    active: true,
    code: '',
    // Scope: 'all' | 'products' | 'categories'
    scopeType: 'all',
    productIds: [],
    categoryIds: [],
    // Sales channels this promo can be redeemed on. Online Orders is the
    // only channel actually enforced today (POS/Kiosk don't have a promo
    // redemption UI yet) — see functions/promotions.js.
    salesChannels: ['online orders'],
    maxTotalUses: '',
    maxUsesPerCustomer: '',
  });

  useEffect(() => {
    if (location) {
      loadPromotions();
      loadCatalog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const loadPromotions = async () => {
    if (!user || !location) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, 'users', user.uid, 'locations', location.id, 'promotions'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ad = a.createdAt?.toMillis?.() || 0;
        const bd = b.createdAt?.toMillis?.() || 0;
        return bd - ad;
      });
      setPromotions(list);
    } catch (e) {
      console.error('Error loading promotions:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    if (!user) return;
    try {
      const db = getFirestore();
      const catSnap = await getDocs(collection(db, 'users', user.uid, 'categories'));
      const cats = catSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const allProducts = [];
      for (const cat of cats) {
        const prodSnap = await getDocs(collection(db, 'users', user.uid, 'categories', cat.id, 'products'));
        prodSnap.docs.forEach((d) => allProducts.push({ id: d.id, categoryId: cat.id, categoryName: cat.name || '', ...d.data() }));
      }
      setCategories(cats);
      setProducts(allProducts);
    } catch (e) {
      console.error('Error loading catalog for promotions:', e);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      type: 'percentage',
      value: '',
      minOrder: '',
      startDate: '',
      endDate: '',
      active: true,
      code: '',
      scopeType: 'all',
      productIds: [],
      categoryIds: [],
      salesChannels: ['online orders'],
      maxTotalUses: '',
      maxUsesPerCustomer: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const openEdit = (promo) => {
    const hasProductScope = Array.isArray(promo.scope?.productIds) && promo.scope.productIds.length > 0;
    const hasCategoryScope = Array.isArray(promo.scope?.categoryIds) && promo.scope.categoryIds.length > 0;
    setForm({
      name: promo.name || '',
      description: promo.description || '',
      type: promo.type || 'percentage',
      value: promo.value?.toString() || '',
      minOrder: promo.minOrder?.toString() || '',
      startDate: promo.startDate ? new Date(promo.startDate.toDate ? promo.startDate.toDate() : promo.startDate).toISOString().split('T')[0] : '',
      endDate: promo.endDate ? new Date(promo.endDate.toDate ? promo.endDate.toDate() : promo.endDate).toISOString().split('T')[0] : '',
      active: promo.active !== false,
      code: promo.code || '',
      scopeType: hasProductScope ? 'products' : (hasCategoryScope ? 'categories' : 'all'),
      productIds: promo.scope?.productIds || [],
      categoryIds: promo.scope?.categoryIds || [],
      salesChannels: Array.isArray(promo.scope?.salesChannels) && promo.scope.salesChannels.length > 0 ? promo.scope.salesChannels : ['online orders'],
      maxTotalUses: promo.usageLimit?.maxTotalUses?.toString() || '',
      maxUsesPerCustomer: promo.usageLimit?.maxUsesPerCustomer?.toString() || '',
    });
    setEditingId(promo.id);
    setShowForm(true);
  };

  const toggleChannel = (channel) => {
    setForm((prev) => {
      const has = prev.salesChannels.includes(channel);
      const next = has ? prev.salesChannels.filter((c) => c !== channel) : [...prev.salesChannels, channel];
      return { ...prev, salesChannels: next };
    });
  };

  const toggleProduct = (productId) => {
    setForm((prev) => {
      const has = prev.productIds.includes(productId);
      return { ...prev, productIds: has ? prev.productIds.filter((id) => id !== productId) : [...prev.productIds, productId] };
    });
  };

  const toggleCategory = (categoryId) => {
    setForm((prev) => {
      const has = prev.categoryIds.includes(categoryId);
      return { ...prev, categoryIds: has ? prev.categoryIds.filter((id) => id !== categoryId) : [...prev.categoryIds, categoryId] };
    });
  };

  const handleSave = async () => {
    if (!user || !location) return;
    if (!form.name.trim()) {
      Swal.fire('Error', 'Promotion name is required.', 'error');
      return;
    }
    const val = parseFloat(form.value);
    if (isNaN(val) || val <= 0) {
      Swal.fire('Error', 'Value must be a positive number.', 'error');
      return;
    }
    if (form.scopeType === 'products' && form.productIds.length === 0) {
      Swal.fire('Error', 'Select at least one product, or switch to "All products".', 'error');
      return;
    }
    if (form.scopeType === 'categories' && form.categoryIds.length === 0) {
      Swal.fire('Error', 'Select at least one category, or switch to "All products".', 'error');
      return;
    }
    if (form.salesChannels.length === 0) {
      Swal.fire('Error', 'Select at least one sales channel.', 'error');
      return;
    }

    const db = getFirestore();
    const id = editingId || doc(collection(db, 'users', user.uid, 'locations', location.id, 'promotions')).id;

    const scope = {
      productIds: form.scopeType === 'products' ? form.productIds : null,
      categoryIds: form.scopeType === 'categories' ? form.categoryIds : null,
      salesChannels: form.salesChannels,
    };
    const usageLimit = {
      maxTotalUses: form.maxTotalUses ? parseInt(form.maxTotalUses, 10) : null,
      maxUsesPerCustomer: form.maxUsesPerCustomer ? parseInt(form.maxUsesPerCustomer, 10) : null,
    };

    const data = {
      name: form.name.trim(),
      description: form.description.trim(),
      type: form.type,
      value: val,
      minOrder: parseFloat(form.minOrder) || 0,
      startDate: form.startDate ? new Date(form.startDate) : null,
      endDate: form.endDate ? new Date(form.endDate) : null,
      active: form.active,
      code: form.code.trim().toUpperCase() || null,
      onlineOnly: form.salesChannels.length === 1 && form.salesChannels[0] === 'online orders',
      scope,
      usageLimit,
      updatedAt: serverTimestamp(),
    };
    if (!editingId) {
      data.createdAt = serverTimestamp();
      data.usageCount = 0;
    }

    try {
      await setDoc(doc(db, 'users', user.uid, 'locations', location.id, 'promotions', id), data, { merge: true });

      // Sync to public store if deployed. Redemption counters/limits are
      // intentionally NOT mirrored here — the public copy is display-only
      // (banners on the storefront); the merchant-owned doc under
      // users/{uid}/locations/{locId}/promotions is the only source of
      // truth read by validatePromoCode at checkout time.
      if (publicStore) {
        await setDoc(doc(db, 'publicStores', location.id, 'promotions', id), {
          name: data.name,
          description: data.description,
          type: data.type,
          value: data.value,
          minOrder: data.minOrder,
          startDate: data.startDate,
          endDate: data.endDate,
          active: data.active,
          code: data.code,
          onlineOnly: data.onlineOnly,
          scope,
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }

      Swal.fire('Saved!', `Promotion "${data.name}" has been saved.`, 'success');
      resetForm();
      await loadPromotions();
    } catch (e) {
      console.error('Error saving promotion:', e);
      Swal.fire('Error', 'Failed to save promotion.', 'error');
    }
  };

  const handleDelete = async (promo) => {
    const result = await Swal.fire({
      title: 'Delete Promotion?',
      text: `Are you sure you want to delete "${promo.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'users', user.uid, 'locations', location.id, 'promotions', promo.id));
      if (publicStore) {
        await deleteDoc(doc(db, 'publicStores', location.id, 'promotions', promo.id));
      }
      Swal.fire('Deleted!', 'Promotion has been removed.', 'success');
      await loadPromotions();
    } catch (e) {
      console.error('Error deleting promotion:', e);
      Swal.fire('Error', 'Failed to delete promotion.', 'error');
    }
  };

  const isPromoActiveNow = (promo) => {
    if (!promo.active) return false;
    const now = new Date();
    if (promo.startDate && promo.startDate.toDate) {
      if (promo.startDate.toDate() > now) return false;
    }
    if (promo.endDate && promo.endDate.toDate) {
      if (promo.endDate.toDate() < now) return false;
    }
    return true;
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const scopeSummary = (promo) => {
    const productIds = promo.scope?.productIds;
    const categoryIds = promo.scope?.categoryIds;
    if (Array.isArray(productIds) && productIds.length > 0) {
      return `${productIds.length} product${productIds.length === 1 ? '' : 's'}`;
    }
    if (Array.isArray(categoryIds) && categoryIds.length > 0) {
      return `${categoryIds.length} categor${categoryIds.length === 1 ? 'y' : 'ies'}`;
    }
    return 'All products';
  };

  const channelSummary = (promo) => {
    const channels = Array.isArray(promo.scope?.salesChannels) && promo.scope.salesChannels.length > 0
      ? promo.scope.salesChannels
      : ['online orders'];
    const labels = { 'online orders': 'Online', POS: 'POS', KIOSK: 'Kiosk' };
    return channels.map((c) => labels[c] || c).join(', ');
  };

  const usageSummary = (promo) => {
    const max = promo.usageLimit?.maxTotalUses;
    if (!max) return null;
    return `${promo.usageCount || 0} / ${max} uses`;
  };

  if (!publicStore) {
    return (
      <div className="alert alert-warning">
        Please deploy your store in the <strong>Site Information</strong> tab to manage promotions.
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="font-weight-bold mb-1">
            <i className="fas fa-tags text-primary mr-2"></i>
            Online Promotions
          </h4>
          <p className="text-muted mb-0">
            Create promotions and control exactly which products, categories, and sales channels they apply to.
          </p>
        </div>
        <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={() => { resetForm(); setShowForm(true); }}>
          <i className="fas fa-plus mr-2"></i>New Promotion
        </button>
      </div>

      {showForm && (
        <div className="card card-outline card-primary mb-4">
          <div className="card-header">
            <h3 className="card-title">{editingId ? 'Edit Promotion' : 'New Promotion'}</h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 form-group">
                <label>Name *</label>
                <input type="text" className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Summer Special" />
              </div>
              <div className="col-md-6 form-group">
                <label>Promo Code (optional)</label>
                <input type="text" className="form-control" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. SUMMER20" />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description visible to customers..." />
            </div>
            <div className="row">
              <div className="col-md-4 form-group">
                <label>Type</label>
                <select className="form-control" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="flat">Flat Amount ($)</option>
                </select>
              </div>
              <div className="col-md-4 form-group">
                <label>Value *</label>
                <input type="number" step="0.01" className="form-control" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === 'percentage' ? '20' : '5.00'} />
              </div>
              <div className="col-md-4 form-group">
                <label>Min. Order ($)</label>
                <input type="number" step="0.01" className="form-control" value={form.minOrder} onChange={(e) => setForm({ ...form, minOrder: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div className="row">
              <div className="col-md-4 form-group">
                <label>Start Date</label>
                <input type="date" className="form-control" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              </div>
              <div className="col-md-4 form-group">
                <label>End Date</label>
                <input type="date" className="form-control" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
              </div>
              <div className="col-md-4 form-group d-flex align-items-center">
                <div className="custom-control custom-switch mt-4">
                  <input type="checkbox" className="custom-control-input" id="promoActive" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  <label className="custom-control-label" htmlFor="promoActive">Active</label>
                </div>
              </div>
            </div>

            <hr />

            {/* ── Product / Category scope ─────────────────────────── */}
            <div className="form-group">
              <label className="font-weight-bold">Applies To</label>
              <div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input type="radio" id="scopeAll" name="scopeType" className="custom-control-input" checked={form.scopeType === 'all'} onChange={() => setForm({ ...form, scopeType: 'all' })} />
                  <label className="custom-control-label" htmlFor="scopeAll">All products</label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input type="radio" id="scopeProducts" name="scopeType" className="custom-control-input" checked={form.scopeType === 'products'} onChange={() => setForm({ ...form, scopeType: 'products' })} />
                  <label className="custom-control-label" htmlFor="scopeProducts">Specific products</label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input type="radio" id="scopeCategories" name="scopeType" className="custom-control-input" checked={form.scopeType === 'categories'} onChange={() => setForm({ ...form, scopeType: 'categories' })} />
                  <label className="custom-control-label" htmlFor="scopeCategories">Specific categories</label>
                </div>
              </div>
            </div>

            {form.scopeType === 'products' && (
              <div className="form-group border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                {products.length === 0 ? (
                  <div className="text-muted small">No products found.</div>
                ) : categories.map((cat) => {
                  const catProducts = products.filter((p) => p.categoryId === cat.id);
                  if (catProducts.length === 0) return null;
                  return (
                    <div key={cat.id} className="mb-2">
                      <div className="text-muted small font-weight-bold">{cat.name}</div>
                      {catProducts.map((p) => (
                        <div className="custom-control custom-checkbox" key={p.id}>
                          <input type="checkbox" className="custom-control-input" id={`prod-${p.id}`} checked={form.productIds.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                          <label className="custom-control-label" htmlFor={`prod-${p.id}`}>{p.name}</label>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {form.scopeType === 'categories' && (
              <div className="form-group border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                {categories.length === 0 ? (
                  <div className="text-muted small">No categories found.</div>
                ) : categories.map((cat) => (
                  <div className="custom-control custom-checkbox" key={cat.id}>
                    <input type="checkbox" className="custom-control-input" id={`cat-${cat.id}`} checked={form.categoryIds.includes(cat.id)} onChange={() => toggleCategory(cat.id)} />
                    <label className="custom-control-label" htmlFor={`cat-${cat.id}`}>{cat.name}</label>
                  </div>
                ))}
              </div>
            )}

            <hr />

            {/* ── Sales channel scope ──────────────────────────────── */}
            <div className="form-group">
              <label className="font-weight-bold">Sales Channels</label>
              <div>
                <div className="custom-control custom-checkbox custom-control-inline">
                  <input type="checkbox" className="custom-control-input" id="channelOnline" checked={form.salesChannels.includes('online orders')} onChange={() => toggleChannel('online orders')} />
                  <label className="custom-control-label" htmlFor="channelOnline">Online Orders</label>
                </div>
                <div className="custom-control custom-checkbox custom-control-inline">
                  <input type="checkbox" className="custom-control-input" id="channelPos" checked={form.salesChannels.includes('POS')} onChange={() => toggleChannel('POS')} />
                  <label className="custom-control-label" htmlFor="channelPos">POS (in-store)</label>
                </div>
                <div className="custom-control custom-checkbox custom-control-inline">
                  <input type="checkbox" className="custom-control-input" id="channelKiosk" checked={form.salesChannels.includes('KIOSK')} onChange={() => toggleChannel('KIOSK')} />
                  <label className="custom-control-label" htmlFor="channelKiosk">Kiosk</label>
                </div>
              </div>
              {(form.salesChannels.includes('POS') || form.salesChannels.includes('KIOSK')) && (
                <small className="text-warning d-block mt-1">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  POS and Kiosk redemption isn't available yet — staff can't currently enter a promo code at checkout on those channels. Enable this now so it's ready once that's added.
                </small>
              )}
            </div>

            <hr />

            {/* ── Usage limits ─────────────────────────────────────── */}
            <div className="row">
              <div className="col-md-6 form-group">
                <label>Max. Total Uses (optional)</label>
                <input type="number" min="1" className="form-control" value={form.maxTotalUses} onChange={(e) => setForm({ ...form, maxTotalUses: e.target.value })} placeholder="Unlimited" />
              </div>
              <div className="col-md-6 form-group">
                <label>Max. Uses Per Customer (optional)</label>
                <input type="number" min="1" className="form-control" value={form.maxUsesPerCustomer} onChange={(e) => setForm({ ...form, maxUsesPerCustomer: e.target.value })} placeholder="Unlimited" />
                <small className="text-muted">Only enforced for logged-in customers; guest checkouts can't be limited individually.</small>
              </div>
            </div>

            <div className="d-flex justify-content-end" style={{ gap: 8 }}>
              <button className="btn btn-outline-secondary" onClick={resetForm}>Cancel</button>
              <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={handleSave}>
                <i className="fas fa-save mr-2"></i>Save Promotion
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="spinner-border text-orange" role="status"><span className="sr-only">Loading...</span></div>
        </div>
      ) : promotions.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-tags fa-4x text-muted mb-4"></i>
          <h4 className="text-muted">No promotions yet</h4>
          <p className="text-muted">Create your first promotion to attract more customers.</p>
        </div>
      ) : (
        <div className="row">
          {promotions.map((promo) => {
            const isActive = isPromoActiveNow(promo);
            const usage = usageSummary(promo);
            return (
              <div className="col-lg-4 col-md-6 mb-4" key={promo.id}>
                <div className={`card h-100 ${isActive ? 'border-success' : 'border-secondary'}`}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="card-title font-weight-bold mb-0">{promo.name}</h5>
                      <span className={`badge badge-${isActive ? 'success' : 'secondary'}`}>{isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                    <p className="card-text text-muted small">{promo.description || 'No description'}</p>
                    <div className="mb-2">
                      <span className="font-weight-bold" style={{ color: '#ff6b35', fontSize: '1.25rem' }}>
                        {promo.type === 'percentage' ? `${promo.value}% OFF` : `$${promo.value.toFixed(2)} OFF`}
                      </span>
                    </div>
                    {promo.code && (
                      <div className="mb-2">
                        <span className="badge badge-info">Code: {promo.code}</span>
                      </div>
                    )}
                    <div className="text-muted small">
                      <div><i className="fas fa-box mr-1"></i>Applies to: {scopeSummary(promo)}</div>
                      <div><i className="fas fa-store mr-1"></i>Channels: {channelSummary(promo)}</div>
                      {promo.minOrder > 0 && <div>Min. order: ${promo.minOrder.toFixed(2)}</div>}
                      {usage && <div>Uses: {usage}</div>}
                      <div>Start: {formatDate(promo.startDate)}</div>
                      <div>End: {formatDate(promo.endDate)}</div>
                    </div>
                  </div>
                  <div className="card-footer bg-transparent d-flex justify-content-between">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(promo)}>
                      <i className="fas fa-edit mr-1"></i>Edit
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(promo)}>
                      <i className="fas fa-trash mr-1"></i>Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SitePromotionsTab;
